/**
 * Sticky Header Custom Element
 * Handles hide-on-scroll-down / reveal-on-scroll-up behavior.
 * Loaded as a deferred script from header section.
 */
(function () {
  if (customElements.get('sticky-header')) return;

  function isElementVisible(element) {
    if (!element) return false;
    const styles = window.getComputedStyle(element);
    if (styles.display === 'none' || styles.visibility === 'hidden') return false;
    const rect = element.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function collectTopChromeMetrics(headerElement) {
    const header = headerElement || document.querySelector('.section-header');
    const headerHeight = isElementVisible(header) ? Math.round(header.getBoundingClientRect().height) : 0;

    let announcementBarsHeight = 0;
    const announcementBars = document.querySelectorAll('.announcement-bar-section');
    announcementBars.forEach((bar) => {
      if (!isElementVisible(bar)) return;
      // Only bars that sit above the header are part of the top chrome the sticky
      // header must clear. A bar rendered below the header (e.g. one placed in the
      // template) scrolls away with the page, so counting it would push the pinned
      // header down and leave a gap beneath the always-sticky bar above it.
      if (header && !(header.compareDocumentPosition(bar) & Node.DOCUMENT_POSITION_PRECEDING)) return;
      announcementBarsHeight += Math.round(bar.getBoundingClientRect().height);
    });

    return {
      headerHeight,
      announcementBarsHeight,
      topChromeHeight: headerHeight + announcementBarsHeight
    };
  }

  function syncTopChromeCssVars(headerElement) {
    const metrics = collectTopChromeMetrics(headerElement);
    document.documentElement.style.setProperty('--header-height', `${metrics.headerHeight}px`);
    document.documentElement.style.setProperty('--announcement-bars-height', `${metrics.announcementBarsHeight}px`);
    document.documentElement.style.setProperty('--top-chrome-height', `${metrics.topChromeHeight}px`);
    return metrics;
  }

  window.sugarThemeSyncTopChrome = function () {
    return syncTopChromeCssVars(document.querySelector('.section-header'));
  };

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      
      this.header = document.querySelector('.section-header');
      if (!this.header) return;

      this.headerIsAlwaysSticky =
        this.getAttribute('data-sticky-type') === 'always' ||
        this.getAttribute('data-sticky-type') === 'reduce-logo-size';
      this.headerBounds = {};
      this.currentScrollTop = 0;
      this.preventReveal = false;
      this.predictiveSearch = this.querySelector('predictive-search');

      this.onScrollHandler = this.onScroll.bind(this);
      this.hideHeaderOnScrollUp = () => (this.preventReveal = true);
      this.addEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      this.onLayoutChange = this.setHeaderHeight.bind(this);

      this.setHeaderHeight();
      this.mobileMediaQuery = window.matchMedia('(max-width: 990px)');
      this.mobileMediaQuery.addEventListener('change', this.onLayoutChange);
      window.addEventListener('resize', this.onLayoutChange, { passive: true });
      document.addEventListener('shopify:section:load', this.onLayoutChange);
      document.addEventListener('shopify:section:unload', this.onLayoutChange);
      document.addEventListener('shopify:section:reorder', this.onLayoutChange);
      this.setupTopChromeObservers();

      if (this.headerIsAlwaysSticky) {
        this.header.classList.add('shopify-section-header-sticky');
      }

      this.createObserver();
      window.addEventListener('scroll', this.onScrollHandler, { passive: true });
    }

    setHeaderHeight() {
      const metrics = syncTopChromeCssVars(this.header);
      if (this.headerIsAlwaysSticky) {
        this.setHeaderStickyOffset(metrics.headerHeight);
      }
    }

    setHeaderStickyOffset(offset) {
      document.documentElement.style.setProperty('--header-sticky-offset', `${offset}px`);
    }

    disconnectedCallback() {
      this.removeEventListener('preventHeaderReveal', this.hideHeaderOnScrollUp);
      window.removeEventListener('scroll', this.onScrollHandler);
      if (this.mobileMediaQuery) {
        this.mobileMediaQuery.removeEventListener('change', this.onLayoutChange);
      }
      window.removeEventListener('resize', this.onLayoutChange);
      document.removeEventListener('shopify:section:load', this.onLayoutChange);
      document.removeEventListener('shopify:section:unload', this.onLayoutChange);
      document.removeEventListener('shopify:section:reorder', this.onLayoutChange);
      if (this.topChromeResizeObserver) {
        this.topChromeResizeObserver.disconnect();
      }
      if (this.topChromeBodyObserver) {
        this.topChromeBodyObserver.disconnect();
      }
    }

    setupTopChromeObservers() {
      if (typeof ResizeObserver !== 'undefined') {
        this.topChromeResizeObserver = new ResizeObserver(() => this.setHeaderHeight());
        if (this.header) {
          this.topChromeResizeObserver.observe(this.header);
        }
        document.querySelectorAll('.announcement-bar-section').forEach((bar) => {
          this.topChromeResizeObserver.observe(bar);
        });
      }

      if (typeof MutationObserver !== 'undefined' && document.body) {
        this.topChromeBodyObserver = new MutationObserver((mutations) => {
          for (let i = 0; i < mutations.length; i++) {
            if (mutations[i].attributeName === 'data-transparent-header-active') {
              this.setHeaderHeight();
              break;
            }
          }
        });
        this.topChromeBodyObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['data-transparent-header-active']
        });
      }
    }

    createObserver() {
      let observer = new IntersectionObserver((entries, observer) => {
        this.headerBounds = entries[0].intersectionRect;
        observer.disconnect();
      });
      observer.observe(this.header);
    }

    onScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;

      if (this.predictiveSearch && this.predictiveSearch.isOpen) return;

      if (scrollTop > this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (this.preventHide) return;
        requestAnimationFrame(this.hide.bind(this));
      } else if (scrollTop < this.currentScrollTop && scrollTop > this.headerBounds.bottom) {
        this.header.classList.add('scrolled-past-header');
        if (!this.preventReveal) {
          requestAnimationFrame(this.reveal.bind(this));
        } else {
          window.clearTimeout(this.isScrolling);
          this.isScrolling = setTimeout(() => {
            this.preventReveal = false;
          }, 66);
          requestAnimationFrame(this.hide.bind(this));
        }
      } else if (scrollTop <= this.headerBounds.top) {
        this.header.classList.remove('scrolled-past-header');
        requestAnimationFrame(this.reset.bind(this));
      }

      this.currentScrollTop = scrollTop;
    }

    hide() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-hidden', 'shopify-section-header-sticky');
      this.closeMenuDisclosure();
      this.closeSearchModal();
      this.setHeaderStickyOffset(0);
    }

    reveal() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.add('shopify-section-header-sticky', 'animate');
      this.header.classList.remove('shopify-section-header-hidden');
      this.setHeaderStickyOffset(this.header.offsetHeight);
    }

    reset() {
      if (this.headerIsAlwaysSticky) return;
      this.header.classList.remove(
        'shopify-section-header-hidden',
        'shopify-section-header-sticky',
        'animate'
      );
      this.setHeaderStickyOffset(0);
    }

    closeMenuDisclosure() {
      this.disclosures = this.disclosures || this.header.querySelectorAll('header-menu');
      this.disclosures.forEach((disclosure) => {
        if (typeof disclosure.close === 'function') disclosure.close();
      });
    }

    closeSearchModal() {
      const dialog = this.header.querySelector('.search-dialog');
      if (dialog && dialog.open) {
        dialog.close();
      }
    }
  }

  // Register after DOMContentLoaded so desktop menus are injected and header has final dimensions.
  // When the class is defined, the existing <sticky-header> element in the DOM gets upgraded
  // immediately and connectedCallback fires with the correct header height.
  function register() {
    customElements.define('sticky-header', StickyHeader);
  }

  if (document.readyState === 'complete') {
    register();
  } else {
    document.addEventListener('DOMContentLoaded', register);
  }
})();
